import { useMutation } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { clearAccessToken, privateApi } from "../libs/axios";
import { queryClient } from "../libs/react-query";
import { isApiError } from "../errors";
import { toastApiError } from "../toast";
import { clearProfile } from "./me";
import { LOGIN_PATH } from "./surfaces";

/**
 * POST /auth/logout/ — revoke the current server-side session and clear the refresh cookie
 * (204). The access token is in memory only, so there is nothing to send beyond the cookie
 * `privateApi` already carries.
 */
export async function logout(): Promise<void> {
  await privateApi.post("/auth/logout/");
}

/**
 * Clear all client-side identity: drop the in-memory access token and remove the cached
 * profile. Shared by explicit logout and by the interceptor's session-loss path (via the
 * logout handler the app registers).
 */
export function clearSession(qc: QueryClient = queryClient): void {
  clearAccessToken();
  clearProfile(qc);
}

/**
 * Logout action for UI. Calls the endpoint, then clears client identity regardless of the
 * call's outcome (a dead session should still log the user out locally) and routes to login.
 * A failed revoke surfaces a toast (except a 401, which already means the session is gone).
 */
export function useLogout() {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: logout,
    onError: (error) => {
      if (isApiError(error) && error.status !== 401) toastApiError(error);
    },
    onSettled: () => {
      clearSession();
      void navigate({ to: LOGIN_PATH });
    },
  });
}
